import { Metamodel } from '../base/model';
import { IModel, IAttribute } from '../base/annotation';
import { LinkedService } from '../linkedservice/linkedservice';

import { AzureKeyVault } from './key-vault';
import { validateAKVLinkedserviceReference } from 'app/shared/utils/linkedservice-validation-util';
import { format } from 'app/shared/utils/string.util';
import { KeyValuePairWithAkv, shellStringResources, Credential, IViolation, restAuthTypes, azureCloudTypeDef, servicePrincipalCredentialTypes } from 'shell-models-lib';
import { adfModelTypeMap } from '../../registries/consts/adf-model-type-map-const';
import { GlobalVariables } from 'app/services/globals';
import { extractRelativeUrlFromFullUrl } from './web-url-uploaded-source.util';
import { extractDsrPathFromRawJson } from 'app/utils/powerqueryDsrUtils';
import { isSharePointUrl } from 'app/shared/utils/sharepoint-url.util';

export const restAuthRefTypes = {
    CredentialRefTypeAuthentication: 'credential',
    InlineRefTypeAuthentication: 'inline'
};

@Metamodel.model({
    name: adfModelTypeMap.rest.linkedServiceType,
    display: [
        format(stringResources.ModelName_LinkedServiceTemplate, stringResources.RestService_DisplayText),
        format(stringResources.ModelName_LinkedServicesTemplate, stringResources.RestService_DisplayText)
    ]
})
export class RestServiceLinkedService extends LinkedService {

    public static readonly MODEL: IModel;

    public static readonly BASE_URL: IAttribute;
    public static readonly USER_NAME: IAttribute;
    public static readonly PASSWORD: IAttribute;
    public static readonly SERVICE_PRINCIPAL_ID: IAttribute;
    public static readonly SERVICE_PRINCIPAL_KEY: IAttribute;
    public static readonly SERVICE_PRINCIPAL_EMBEDDED_CERT: IAttribute;
    public static readonly SERVICE_PRINCIPAL_EMBEDDED_CERT_PASSWORD: IAttribute;
    public static readonly TENANT: IAttribute;
    public static readonly AAD_RESOURCE_ID: IAttribute;
    public static readonly AZURE_CLOUD_TYPE: IAttribute;
    public static readonly AUTH_HEADER: IAttribute;
    public static readonly TOKEN_ENDPOINT: IAttribute;
    public static readonly CLIENT_ID: IAttribute;
    public static readonly CLIENT_SECRET: IAttribute;
    public static readonly SCOPE: IAttribute;
    public static readonly RESOURCE: IAttribute;

    @Metamodel.attribute({
        display: stringResources.ModelName_BaseUrl,
        association: 'BASE_URL',
        templatable: true
    })
    public url?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_EnableServerCertificateValidation
    })
    public enableServerCertificateValidation?: boolean = true;

    @Metamodel.attribute({
        display: stringResources.ModelName_AuthenticationType
    })
    public authenticationType?: string = restAuthTypes.Basic;

    @Metamodel.attribute({
        display: shellStringResources.Credential,
        reference: true,
        constraint: {
            validator: (linkedService: RestServiceLinkedService, referencedCredential: Credential): IViolation[] | undefined => {
                // run cred validator for MSI if credential field is populated or for UAMI which must have a cred reference
                if (linkedService
                    && (
                        (linkedService.authenticationType === restAuthTypes.ManagedServiceIdentity && linkedService.credential)
                        || linkedService.authenticationType === restAuthTypes.UserAssignedManagedIdentity
                        || (GlobalVariables.enableConnectorUserAuthCred && linkedService.authenticationType === restAuthTypes.UserAuth)
                    )
                    && !referencedCredential) {
                    return [{ message: () => shellStringResources.Error_InvalidCredentialReferenced }];
                }
                return undefined;
            }
        }
    })
    public credential?: Credential;

    public authReferenceMethod?: string = restAuthRefTypes.InlineRefTypeAuthentication;

    @Metamodel.attribute({
        display: stringResources.ModelName_UserName,
        association: 'USER_NAME',
        templatable: true
    })
    public userName?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_Password,
        constraint: {
            validator: validateAKVLinkedserviceReference
        },
        association: 'PASSWORD',
        templatable: true,
        accepts: ['String']
    })
    public password?: string | AzureKeyVault;

    @Metamodel.attribute({
        display: stringResources.ModelName_ServicePrincipalID,
        association: 'SERVICE_PRINCIPAL_ID',
        templatable: true
    })
    public servicePrincipalID?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_ServicePrincipalKey,
        constraint: {
            validator: validateAKVLinkedserviceReference
        },
        association: 'SERVICE_PRINCIPAL_KEY',
        templatable: true,
        accepts: ['String']
    })
    public servicePrincipalKey?: string | AzureKeyVault;

    @Metamodel.attribute({
        display: stringResources.ModelName_ServicePrincipalCredentialType
    })
    public servicePrincipalCredentialType?: keyof typeof servicePrincipalCredentialTypes;

    @Metamodel.attribute({
        display: stringResources.ModelName_ServicePrincipalEmbeddedCert,
        constraint: {
            validator: validateAKVLinkedserviceReference
        },
        association: 'SERVICE_PRINCIPAL_EMBEDDED_CERT',
        templatable: true,
        accepts: ['String']
    })
    public servicePrincipalEmbeddedCert?: string | AzureKeyVault;

    @Metamodel.attribute({
        display: stringResources.ModelName_ServicePrincipalEmbeddedCertPassword,
        constraint: {
            validator: validateAKVLinkedserviceReference
        },
        association: 'SERVICE_PRINCIPAL_EMBEDDED_CERT_PASSWORD',
        templatable: true,
        accepts: ['String']
    })
    public servicePrincipalEmbeddedCertPassword?: string | AzureKeyVault;

    @Metamodel.attribute({
        display: stringResources.ModelName_Tenant,
        association: 'TENANT',
        templatable: true
    })
    public tenant?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_AADResourceId,
        association: 'AAD_RESOURCE_ID',
        templatable: true
    })
    public aadResourceId?: string;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_AzureCloudType,
        association: 'AZURE_CLOUD_TYPE',
        templatable: true
    })
    public azureCloudType?: azureCloudTypeDef;

    @Metamodel.attribute({
        display: stringResources.ModelName_AuthHeaders,
        association: 'AUTH_HEADER'
    })
    public authHeaders?: KeyValuePairWithAkv[] = [];

    @Metamodel.attribute({
        display: stringResources.TokenEndpoint,
        association: 'TOKEN_ENDPOINT',
        templatable: true
    })
    public tokenEndpoint?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_ClientId,
        association: 'CLIENT_ID',
        templatable: true
    })
    public clientID?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_ClientSecret,
        association: 'CLIENT_SECRET',
        constraint: {
            validator: validateAKVLinkedserviceReference
        },
        templatable: true
    })
    public clientSecret?: string | AzureKeyVault;

    @Metamodel.attribute({
        display: stringResources.Scope,
        association: 'SCOPE',
        templatable: true
    })
    public scope?: string;

    @Metamodel.attribute({
        display: stringResources.Resource,
        association: 'RESOURCE',
        templatable: true
    })
    public resource?: string;

    public authHeaderBannerMessage: string = stringResources.Rest_Auth_Header_May_Miss_Info_On_Create

    /**
     * Transient (non-persisted) field holding the absolute URL that PQO returned for an
     * uploaded-source connection. The Trident connection picker peeks at this to seed
     * the dataset's `relativeUrl` so the user does not have to re-type it. Not annotated
     * with `@Metamodel.attribute`, so it is excluded from serialization.
     */
    private _uploadedFullUrlFromPqo?: string;

    /** Stash the absolute upload URL that PQO emitted. Called by `UnifiedPQConnectionService`. */
    public setUploadedFullUrlFromPqo(fullUrl: string | undefined): void {
        this._uploadedFullUrlFromPqo = fullUrl;
    }

    /** Non-destructive: signals this LS came from the PQ upload-commit path. */
    public hasUploadedFullUrlFromPqo(): boolean {
        return !!this._uploadedFullUrlFromPqo;
    }

    /**
     * One-shot: returns the relative URL derived from the stashed full URL and clears the
     * stashed value. The base used for subtraction is `this.url` when populated, otherwise
     * the `path` advertised by the linked service's stringified `datasourceReference`
     * (which Trident populates before the typed attributes are hydrated). Returns
     * `undefined` when nothing was stashed or the derivation produced no relative portion.
     */
    public consumeRelativeUrlFromUploadedFullUrl(): string | undefined {
        const full = this._uploadedFullUrlFromPqo;
        this._uploadedFullUrlFromPqo = undefined;
        const baseUrl = this.url ?? extractDsrPathFromRawJson(this.raw);
        return extractRelativeUrlFromFullUrl(full, baseUrl);
    }

    constructor(name?: string, description?: string) {
        super(name, description);
    }

    public isOldServicePrincipalResource: boolean = false;

    public get isSharePointUrl(): boolean {
        return isSharePointUrl(this.url);
    }
}
